import { useEffect, useMemo, useRef, useState } from 'react';
import { isReplyWindowOpen } from '../bridge';
import { api, ApiError } from '../api';
import { getSocket } from '../socket';
import { useChat } from '../store';
import { useChatPrefs } from '../utils/chatPrefs';
import { clearDraft, getDraft, setDraft as persistDraft } from '../utils/drafts';
import { VoiceRecorder } from './VoiceRecorder';
import { Avatar } from './Avatar';
import { IconClose, IconMic, IconPaperclip, IconReply, IconSend } from './Icon';

interface MessageInputProps {
  conversationId: string;
}

const TYPING_TIMEOUT = 2500;

export function MessageInput({ conversationId }: MessageInputProps) {
  // Initialize from persisted store so a page reload keeps the draft for
  // whichever conversation the user opens. Subsequent conversation switches
  // re-load via the conversationId-keyed effect below.
  const [draft, setDraft] = useState(() => getDraft(conversationId));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mentionState, setMentionState] = useState<{ query: string; start: number } | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [pickedMentions, setPickedMentions] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingRef = useRef(false);
  const typingTimerRef = useRef<number | null>(null);

  const me = useChat((s) => s.me);
  const users = useChat((s) => s.users);
  const prefs = useChatPrefs();
  // The parent message of an in-progress reply, scoped to this
  // conversation. setReplyingTo(convId, message) populates it from
  // anywhere; the composer reads it and shows a small preview bar
  // above the textarea, plus passes its id as parentId on send.
  const replyingTo = useChat((s) => s.replyingByConv[conversationId] ?? null);
  const setReplyingTo = useChat((s) => s.setReplyingTo);

  // The @handle for a participant: prefer their username, fall back to the
  // local part of their email so users without a set username still mention.
  function handleFor(participantId: string, email: string): string {
    const u = users.find((x) => x.id === participantId);
    if (u?.username) return u.username;
    return email.split('@')[0]!;
  }
  const conversation = useChat((s) =>
    s.conversations.find((c) => c.id === conversationId),
  );

  const mentionMatches = useMemo(() => {
    if (!mentionState || !conversation) return [];
    const q = mentionState.query.toLowerCase();
    return conversation.participants
      .filter((p) => p.id !== me?.id)
      .filter((p) => {
        const dn = (p.displayName ?? '').toLowerCase();
        const handle = handleFor(p.id, p.email).toLowerCase();
        return dn.includes(q) || handle.includes(q);
      })
      .slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionState, conversation, me?.id, users]);

  // Switching conversations: load the persisted draft for the new one,
  // not blanking. The previous conversation's draft was already persisted
  // by the changes-effect below as the user typed.
  useEffect(() => {
    setDraft(getDraft(conversationId));
    setError(null);
    stopTyping();
    textareaRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Persist the draft on every change. localStorage writes are cheap and
  // synchronous; debouncing would buy little here. Empty drafts get
  // removed from the store, not stored as empty strings.
  useEffect(() => {
    persistDraft(conversationId, draft);
  }, [conversationId, draft]);

  function startTyping() {
    if (!typingRef.current) {
      typingRef.current = true;
      getSocket().emit('typing:start', { conversationId });
    }
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    typingTimerRef.current = window.setTimeout(stopTyping, TYPING_TIMEOUT);
  }

  function stopTyping() {
    if (typingRef.current) {
      typingRef.current = false;
      getSocket().emit('typing:stop', { conversationId });
    }
    if (typingTimerRef.current) {
      window.clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  }

  async function send() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setSending(true);
    setError(null);
    stopTyping();
    // Validate the picked mention IDs are still actually @-referenced in the body;
    // drops any whose @-text was deleted.
    const stillReferenced = pickedMentions.filter((uid) => {
      const p = conversation?.participants.find((x) => x.id === uid);
      if (!p) return false;
      const handle = handleFor(p.id, p.email);
      const dn = (p.displayName ?? '').replace(/\s+/g, '');
      return trimmed.includes(`@${handle}`) || (dn && trimmed.includes(`@${dn}`));
    });
    try {
      await api.sendMessage(
        conversationId,
        trimmed,
        replyingTo?.id ?? undefined,
        stillReferenced,
      );
      setDraft('');
      setPickedMentions([]);
      // Successful send clears the reply state — the user has expressed
      // their reply and the next message is unattached unless they
      // re-arm.
      if (replyingTo) setReplyingTo(conversationId, null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send');
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  }

  async function uploadFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      await api.uploadAttachment(conversationId, file, draft.trim());
      setDraft('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 413) {
        setError('File is too large (50 MB max).');
      } else if (err instanceof ApiError && err.code === 'storage_not_configured') {
        setError('File uploads are not yet configured. Ask your admin to set up R2.');
      } else {
        setError('Upload failed. Try again.');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) {
          e.preventDefault();
          void uploadFile(f);
          return;
        }
      }
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void uploadFile(f);
  }

  function pickMention(participantId: string) {
    if (!mentionState || !conversation) return;
    const p = conversation.participants.find((x) => x.id === participantId);
    if (!p) return;
    const handle = handleFor(p.id, p.email);
    const before = draft.slice(0, mentionState.start);
    const afterIdx = mentionState.start + 1 + mentionState.query.length;
    const after = draft.slice(afterIdx);
    const insert = `@${handle} `;
    const next = before + insert + after;
    setDraft(next);
    setPickedMentions((prev) => (prev.includes(p.id) ? prev : [...prev, p.id]));
    setMentionState(null);
    setMentionIndex(0);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      const pos = before.length + insert.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }

  function detectMention(value: string, caret: number) {
    // Look back from the caret for a @-trigger that's at start-of-string or
    // preceded by whitespace.
    let i = caret - 1;
    while (i >= 0 && /[A-Za-z0-9_.\-]/.test(value[i]!)) i--;
    if (i < 0 || value[i] !== '@') {
      setMentionState(null);
      setMentionIndex(0);
      return;
    }
    if (i > 0 && !/\s/.test(value[i - 1]!)) {
      setMentionState(null);
      setMentionIndex(0);
      return;
    }
    setMentionState({ query: value.slice(i + 1, caret), start: i });
    setMentionIndex(0);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionState && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionMatches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickMention(mentionMatches[mentionIndex]!.id);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionState(null);
        return;
      }
    }
    // Escape with no mention typeahead open: cancel a pending reply.
    if (e.key === 'Escape' && replyingTo) {
      e.preventDefault();
      setReplyingTo(conversationId, null);
      return;
    }
    if (e.key === 'Enter') {
      // enterToSend=true (default): Enter sends, Shift+Enter inserts newline.
      // enterToSend=false: Enter inserts newline, Cmd/Ctrl+Enter sends.
      const wantsSend = prefs.enterToSend
        ? !e.shiftKey
        : e.metaKey || e.ctrlKey;
      if (wantsSend) {
        e.preventDefault();
        if (canSend) void send();
      }
    }
  }

  function autoSize(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }

  // Cleanup typing on unmount.
  useEffect(() => {
    return () => stopTyping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const replyWindowClosed =
    conversation?.bridge?.network === 'whatsapp' &&
    !isReplyWindowOpen(conversation.bridge.lastInboundAt);
  const canSend = !sending && draft.trim().length > 0 && !replyWindowClosed;

  return (
    <div
      className={`px-3 pt-1 pb-3 transition-all duration-300 ease-spring ${
        dragging ? 'bg-indigo-500/5' : ''
      }`}
      onDragEnter={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {error && (
        <p className="text-xs text-rose-400 mb-2 px-1 animate-fade-up">{error}</p>
      )}
      {uploading && (
        <p className="text-xs text-zinc-400 mb-2 px-1 animate-fade-up">Uploading…</p>
      )}

      {/* Reply preview — shown while a Reply action is pending. The
          parent message id rides along on send and gets dropped after.
          Esc or the X button cancels. */}
      {replyingTo && (
        <div className="mb-1.5 flex items-stretch gap-2 rounded-md hairline bg-zinc-900/60 px-2 py-1.5 animate-fade-up">
          <span className="w-1 self-stretch rounded-full bg-indigo-400/70" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
              <IconReply size={10} className="text-zinc-400" />
              <span>
                Replying to{' '}
                <span className="font-medium text-zinc-300">
                  {(() => {
                    if (replyingTo.senderId === me?.id) return 'yourself';
                    const p = conversation?.participants.find(
                      (x) => x.id === replyingTo.senderId,
                    );
                    return p?.displayName ?? p?.email?.split('@')[0] ?? 'Someone';
                  })()}
                </span>
              </span>
            </div>
            <p className="text-xs text-zinc-300 truncate">
              {replyingTo.deletedAt
                ? '(deleted)'
                : replyingTo.body || replyingTo.attachments?.[0]?.filename || '(media)'}
            </p>
          </div>
          <button
            onClick={() => setReplyingTo(conversationId, null)}
            className="self-start text-zinc-500 hover:text-zinc-200 transition p-0.5"
            aria-label="Cancel reply"
            title="Cancel reply (Esc)"
          >
            <IconClose size={12} />
          </button>
        </div>
      )}

      {/* Mention typeahead */}
      {mentionState && mentionMatches.length > 0 && (
        <div className="mb-1.5 glass-chrome hairline rounded-2xl overflow-hidden shadow-elevation animate-fade-up">
          {mentionMatches.map((p, i) => (
            <button
              key={p.id}
              onMouseDown={(e) => {
                e.preventDefault();
                pickMention(p.id);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors duration-200 ease-swift ${
                i === mentionIndex ? 'bg-zinc-900/80' : 'hover:bg-zinc-900/50'
              }`}
            >
              <Avatar id={p.id} displayName={p.displayName} email={p.email} size={20} />
              <span className="text-sm text-zinc-100 truncate">
                {p.displayName ?? p.email}
              </span>
              <span className="text-xs text-zinc-500 truncate">
                @{handleFor(p.id, p.email)}
              </span>
            </button>
          ))}
        </div>
      )}

      {recording ? (
        <VoiceRecorder
          onComplete={(file) => {
            setRecording(false);
            void uploadFile(file);
          }}
          onCancel={() => setRecording(false)}
        />
      ) : (
        // Double-bezel composer — outer shell + inner pane with concentric radii.
        <div
          className={`p-1 rounded-[1.5rem] transition-all duration-300 ease-spring ${
            dragging
              ? 'bg-indigo-500/15 hairline-strong'
              : 'bg-zinc-900/40 hairline'
          }`}
        >
          <div className="flex items-end gap-1.5 bg-zinc-900/80 rounded-[calc(1.5rem-0.25rem)] px-2 py-1.5 shadow-inset-hairline focus-within:bg-zinc-900 transition-all duration-300 ease-swift">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadFile(f);
              }}
            />
            <ComposerIconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Attach a file"
            >
              <IconPaperclip size={16} strokeWidth={1.5} />
            </ComposerIconButton>
            <ComposerIconButton
              onClick={() => {
                if (uploading) return;
                setRecording(true);
              }}
              disabled={uploading}
              title="Record a voice message"
            >
              <IconMic size={16} strokeWidth={1.5} />
            </ComposerIconButton>
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                autoSize(e.currentTarget);
                detectMention(e.target.value, e.currentTarget.selectionStart ?? e.target.value.length);
                if (e.target.value.length > 0) startTyping();
                else stopTyping();
              }}
              onKeyUp={(e) =>
                detectMention(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)
              }
              onClick={(e) =>
                detectMention(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)
              }
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              placeholder={dragging ? 'Drop to upload…' : 'Message…'}
              rows={1}
              className="flex-1 bg-transparent border-none outline-none resize-none text-sm py-1.5 px-1 max-h-48 placeholder:text-zinc-500"
            />
            {/* Magnetic send button — scales + tints when armed. */}
            <button
              onClick={send}
              disabled={!canSend}
              aria-label="Send message"
              title="Send message"
              className={`group self-end w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 ease-overshoot active:scale-[0.92] ${
                canSend
                  ? 'bg-gradient-to-br from-indigo-400 to-indigo-600 text-white shadow-glow-indigo'
                  : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              }`}
            >
              <IconSend
                size={15}
                strokeWidth={1.75}
                className={`transition-transform duration-300 ease-overshoot ${
                  canSend ? 'group-hover:translate-x-0.5 group-hover:-translate-y-px' : ''
                }`}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ComposerIconButton({
  onClick,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="group self-end w-9 h-9 rounded-xl text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/80 disabled:opacity-50 transition-all duration-300 ease-spring active:scale-[0.92] flex items-center justify-center"
    >
      <span className="transition-transform duration-300 ease-spring group-hover:scale-110">
        {children}
      </span>
    </button>
  );
}
